<template lang="html">
  <ProductDetailsSkeleton v-if="showSkeleton" />

  <div
    v-else-if="product"
    class="product-details w-full max-w-(--ui-container) px-4 pt-6 pb-28 sm:m-auto sm:px-6 lg:pb-10"
  >
    <ProductBreadcrumbs
      :value="product"
      class="mb-4"
    />

    <div
      class="product-details__body mb-10 items-start gap-8 lg:mb-12 lg:grid lg:grid-cols-[minmax(0,1.15fr)_minmax(320px,0.85fr)] lg:gap-10"
    >
      <ProductGallery
        class="product-details__gallery"
        :value="product"
      />

      <aside
        class="product-details__buy-box flex flex-col gap-4 lg:sticky lg:top-[calc(var(--ui-header-height)+1rem)] lg:self-start"
      >
        <div class="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted">
          <span v-if="article">
            Артикул: {{ article }}
          </span>
          <span
            v-if="product.inStock"
            class="font-medium text-success"
          >
            В наличии
          </span>
          <span
            v-else
            class="font-medium text-error"
          >
            Нет в наличии
          </span>
        </div>

        <div class="flex items-start justify-between gap-4">
          <h1 class="product-details__title text-2xl leading-tight font-bold">
            {{ product?.wellKnownAttributes?.Name }}
          </h1>
          <UButton
            :icon="isFavorite ? 'i-heroicons-heart-solid' : 'i-lucide-heart'"
            :color="isFavorite ? 'error' : 'neutral'"
            variant="ghost"
            size="xl"
            class="shrink-0"
            @click.stop.prevent="onFavoriteToggle"
          />
        </div>

        <div class="flex flex-wrap items-center gap-2">
          <PromoBadge
            :badge="product?.promoBadge"
            :cart-promo="product?.hasCartPromo && !product?.hasCashbackPromo"
            :cashback="product?.hasCashbackPromo"
          />
        </div>

        <PromoPrice
          v-if="product"
          :value="product"
          :quantity="cartQuantity"
          class="product-details__price text-2xl font-semibold"
        />

        <div
          v-if="product?.variants"
          key="variants"
          class="product-details__variants"
        >
          <VariantsNavigation
            :value="product"
            :active-id="id"
          />
        </div>

        <SizeTableSection :value="product" />

        <CartSection
          v-if="product.inStock"
          class="hidden lg:flex"
          :value="product"
        />

        <BuyBoxTrustStrip
          :show-promo="Boolean(product.promoTerms?.trim())"
          @open-promo="openPromoTerms"
        />
      </aside>
    </div>

    <ProductContentTabs
      ref="contentTabs"
      :value="product"
      class="mb-8"
    />

    <ProductRecommendations
      :key="id"
      :value="product"
      class="mb-10"
    />

    <ProductMobileBuyBar :value="product" />
  </div>
</template>

<script setup lang="ts">
import { useEcommerce } from '#Analytics/composables/useEcommerce'
import { mapCatalogProduct } from '#Analytics/utils/ecommerceMapper'
import BuyBoxTrustStrip from '#Products/components/Detail/BuyBoxTrustStrip.vue'
import CartSection from '#Products/components/Detail/CartSection.vue'
import ProductBreadcrumbs from '#Products/components/Detail/ProductBreadcrumbs.vue'
import ProductContentTabs from '#Products/components/Detail/ProductContentTabs.vue'
import ProductDetailsSkeleton from '#Products/components/Detail/ProductDetailsSkeleton.vue'
import ProductGallery from '#Products/components/Detail/ProductGallery.vue'
import ProductMobileBuyBar from '#Products/components/Detail/ProductMobileBuyBar.vue'
import ProductRecommendations from '#Products/components/Detail/ProductRecommendations.vue'
import SizeTableSection from '#Products/components/Detail/SizeTableSection.vue'
import VariantsNavigation from '#Products/components/Detail/VariantsNavigation.vue'
import PromoBadge from '#Promotion/components/PromoBadge.vue'
import PromoPrice from '#Promotion/components/PromoPrice.vue'

definePageMeta({
  name: 'ProductsDetails',
})

const { service, routing } = useProducts()
const route = useRoute()
const id = computed(() => routing.extractIdFromSlug(route.params.id as string))
const { toggle: toggleFavorite } = useFavoritesCore()
const { getItem } = useCart()

const contentTabs = useTemplateRef('contentTabs')

/**
 * `lazy: true` — client navigation is not blocked by the product fetch
 * (skeleton shows immediately). SSR still waits for data (SEO / first paint).
 *
 * Key is reactive and includes `id` so each variant/product has its own cache
 * entry. Do not add `watch: [id]` on top — the key change already re-fetches,
 * and Nuxt copies previous data into the new key as a placeholder; skeleton
 * must key off `product.id === id`, not bare `pending`, or variant switches
 * flash: old content → skeleton → new content.
 */
const {
  data: product,
  status,
  error,
} = await useAsyncData(
  () => `product-${id.value}`,
  () => service.get(id.value),
  {
    lazy: true,
    // Keep payload entries across variant switches (custom getCachedData
    // disables Nuxt's purge-on-unmount). Revisited variants resolve sync.
    getCachedData: (key, nuxtApp) => nuxtApp.payload.data[key],
  },
)

/** Skeleton only when we don't yet have data for the current route id. */
const showSkeleton = computed(
  () => product.value?.id !== id.value && status.value !== 'error',
)

function productNotFoundError() {
  return createError({
    statusCode: 404,
    statusMessage: 'Product not found',
  })
}

// SSR: await + lazy still waits on the server — fail fast if missing.
if (import.meta.server && (error.value || !product.value)) {
  throw productNotFoundError()
}

if (import.meta.server && product.value) {
  await useProductSlugRedirect(product)
}

// Client: resolve 404 / canonical slug after the non-blocking fetch settles.
if (import.meta.client) {
  watch(
    [status, product, error],
    async ([s, p, err]) => {
      if (s === 'pending' || s === 'idle') return

      if (err || !p) {
        showError(productNotFoundError())
        return
      }

      await useProductSlugRedirect(product)
    },
    { immediate: true },
  )
}

useProductDetailsSeo(product)

const model = ref(product.value)

watch(
  product,
  (next) => {
    if (next) model.value = next
  },
  { immediate: true },
)

const article = computed(
  () => product.value?.wellKnownAttributes?.Article?.trim() || '',
)

/** Cart qty for line total in PromoPrice; 1 when SKU is not in the cart. */
const cartQuantity = computed(() => {
  const amount = getItem(product.value?.id)?.amount ?? 0
  return amount > 0 ? amount : 1
})

const ecommerce = useEcommerce()

function trackDetail() {
  if (product.value) ecommerce.detail(mapCatalogProduct(product.value))
}

onMounted(trackDetail)
watch(product, (next, prev) => {
  if (next && next.id !== prev?.id) trackDetail()
})

const isFavorite = computed(() => model.value?.isFavorite)

async function onFavoriteToggle() {
  if (product.value && model.value) {
    await toggleFavorite(model.value)
  }
}

async function openPromoTerms() {
  const opened = contentTabs.value?.openTab('promo')
  if (!opened) return

  await nextTick()
  document
    .getElementById('product-content-tabs')
    ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}
</script>
